import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import searchSynonymsServiceApi, {
  type CreateSynonymPayload,
  type MappingType,
  type SynonymRecord,
  type UpdateSynonymArgs,
} from '../api/search-synonyms.service.api';

/**
 * React-query hooks for the search-synonyms domain. All consumers should reach
 * the API through these hooks.
 *
 * Mappings are global (not org-scoped) and ROOT-only; a non-ROOT caller surfaces
 * as a rejected query/mutation (the backend returns 403). Mutations invalidate
 * the whole domain — records can change `mappingType` and re-sort, so a full
 * invalidate is simpler and safer than patching the cache in place.
 */

export const synonymsKeys = {
  all: ['searchSynonyms'] as const,
  list: (mappingType?: MappingType) => [...synonymsKeys.all, 'list', mappingType ?? null] as const,
  detail: (id: string | null | undefined) => [...synonymsKeys.all, 'detail', id] as const,
};

type QueryOpts<T> = Partial<Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>>;

// ---------- Reads ----------

export function useSynonyms(mappingType?: MappingType, options: QueryOpts<SynonymRecord[]> = {}) {
  return useQuery<SynonymRecord[]>({
    queryKey: synonymsKeys.list(mappingType),
    queryFn: ({ signal }) => searchSynonymsServiceApi.getSynonyms({ mappingType, signal }),
    ...options,
  });
}

// ---------- Mutations ----------

export function useCreateSynonym() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSynonymPayload) => searchSynonymsServiceApi.createSynonym(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: synonymsKeys.all });
    },
  });
}

export function useUpdateSynonym() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: UpdateSynonymArgs) => searchSynonymsServiceApi.updateSynonym(args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: synonymsKeys.all });
    },
  });
}

export function useDeleteSynonym() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => searchSynonymsServiceApi.deleteSynonym({ id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: synonymsKeys.all });
    },
  });
}
